'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * A route change is exposed to GTM without loading Google tags directly from
 * the app. The GTM workspace must map this event to its single GA4 page_view
 * tag if enhanced measurement is not already handling browser history.
 */
export default function TrafficTracking() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedRoute = useRef<string | null>(null);
  const [trackingReady, setTrackingReady] = useState(false);

  useEffect(() => {
    const markReady = () => setTrackingReady(true);

    if (window.__zapiTrackingReady) markReady();
    window.addEventListener('zapi:tracking-ready', markReady);

    return () => window.removeEventListener('zapi:tracking-ready', markReady);
  }, []);

  useEffect(() => {
    if (!trackingReady) return;

    const search = searchParams.toString();
    const pagePath = `${pathname}${search ? `?${search}` : ''}`;

    // The initial pageview belongs to the GTM Google tag. Keep this guard
    // stable even when React replays effects in development.
    if (lastTrackedRoute.current === null) {
      lastTrackedRoute.current = pagePath;
      return;
    }

    if (lastTrackedRoute.current === pagePath) return;

    lastTrackedRoute.current = pagePath;
    const pageLocation = window.location.href;
    const pageTitle = document.title;

    window.dataLayer?.push({
      event: 'nextjs_route_change',
      page_location: pageLocation,
      page_path: pagePath,
      page_title: pageTitle,
    });
    window.fbq?.('track', 'PageView');
  }, [pathname, searchParams, trackingReady]);

  return null;
}
